import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { useSheet } from '@shared/hooks/use-sheet'
import type { ApiResponse } from '@shared/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'router-v6'
import { v4 as uuidv4 } from 'uuid'
import { DestructiveConfirmPopupWithPermission } from 'v2/shared/components/destructive-confirm-popup-with-permission'
import { DeletePopupDescription } from 'v2/shared/components/misc'
import { SideSheet } from 'v2/shared/components/side-sheet'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  Credential,
  IPAccessControlList,
  ROriginationURIInternal,
  RPlivoApplicationInternal,
} from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { errorToast, generateToastsForMutation, successToast } from 'v2/utils/toasts'
import { buildApplicationFormDefaults, resolveSubaccountPayload } from './application-form-defaults'
import { CredentialModal } from './components/credential-modal'
import {
  type DeleteApplicationChoice,
  DeleteApplicationOptions,
} from './components/delete-application-options'
import { IPACLModal } from './components/ip-acl-modal'
import { LinkedEndpointsTable } from './components/linked-endpoints-table'
import { LinkedNumbersTable } from './components/linked-numbers-table'
import { OriginationURIModal } from './components/origination-uri-modal'
import { fetchAllAccountAcls, resolveManagedAcl } from './managed-acl'
import { buildOutboundDetachPatch } from './outbound-trunk-payload'
import {
  deriveOutboundAuthType,
  detectPlatformFromUri,
  isCustomSipLocked,
  isManagedAclPlatform,
  SIP_PLATFORMS,
  type SipPlatformKey,
} from './platform-config'
import { isLockedCustomPlatform } from './platform-field.config'
import {
  useApplicationDetailQuery,
  useCreateApplicationMutation,
  useCreateTrunkMutation,
  useCredentialsQuery,
  useDeleteApplicationMutation,
  useDeleteTrunkMutation,
  useForgetSipTrunkPlatformMutation,
  useIPAccessControlListsQuery,
  useOriginationURIsQuery,
  useRecordSipTrunkPlatformMutation,
  useSipTrunkPlatformsQuery,
  useTrunkDetailQuery,
  useUpdateApplicationMutation,
  useUpdateTrunkMutation,
} from './queries'
import {
  canRecordPlatform,
  getTrunkRef,
  isUnrecognizedRecordedPlatform,
  resolveTrunkPlatform,
  unwrapTrunk,
} from './resolve-trunk-platform'
import {
  type ApplicationTableItem,
  ApplicationType,
  type CreateApplicationFormValues,
  type CreateInboundTrunkPayload,
  type CreateTrunkFormValues,
  type InboundTrunkFormValues,
  type RTrunk,
  type UnifiedApplicationFormValues,
  type UpdateInboundTrunkPayload,
} from './types'
import { getUnifiedApplicationFormFields } from './unified-form.config'

type SheetDataType = boolean | 'inbound' | 'outbound' | ApplicationTableItem

const ApplicationSheet = () => {
  const { sheetData, closeSheet, isOpen } = useSheet<SheetDataType>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Custom SIP Platform and trunk messaging sit under the standard enterprise
  // gate, which already means NEW SIGNUPS ONLY: isEnterpriseLocked is
  // non-Enterprise AND org created on/after ENTERPRISE_GATING_CUTOFF, so every
  // pre-cutoff org keeps both regardless of plan (product call 2026-07-29, and
  // Vyas again on 2026-07-30 for Custom SIP specifically). No separate marker.
  const { isEnterpriseLocked } = useUserPlan()
  // The flag can only GRANT Custom SIP Platform, never take it away: it is for the
  // one case the dated gate cannot express, a post-cutoff signup on Free or
  // Professional approved for its own SIP platform without changing plan. Applied
  // here rather than inside isEnterpriseLocked so it cannot leak into the other
  // enterprise-gated screens (Messaging, geo permissions, Sender IDs).
  const { customSipPlatformEnabled } = useFeatureFlags()
  // Region-aware since 2026-08-14: India is open on every plan, US unchanged. The
  // rule itself lives in platform-config so it can be unit-tested (isCustomSipLocked).
  const isCustomPlatformLocked = isCustomSipLocked({
    isEnterpriseLocked,
    customSipPlatformEnabled,
    region: getLocalAuth().region,
  })
  const isTrunkMessagingLocked = isEnterpriseLocked

  // Modal states for IP ACL, Credential, and Origination URI management
  const [ipAclModalOpen, setIpAclModalOpen] = useState(false)
  const [credentialModalOpen, setCredentialModalOpen] = useState(false)
  const [originationURIModalOpen, setOriginationURIModalOpen] = useState(false)
  const [editingIPACL, setEditingIPACL] = useState<IPAccessControlList | null>(null)
  const [editingCredential, setEditingCredential] = useState<Credential | null>(null)
  const [editingOriginationURI, setEditingOriginationURI] =
    useState<ROriginationURIInternal | null>(null)
  // Platform captured when the URI modal opens (drives its templated input)
  const [uriModalPlatform, setUriModalPlatform] = useState<SipPlatformKey | null>(null)

  const { mutate: createApplicationMutation, isPending: isCreatingApplication } =
    useCreateApplicationMutation()
  const { mutate: updateApplicationMutation, isPending: isUpdatingApplication } =
    useUpdateApplicationMutation()
  const { mutate: deleteApplicationMutation, isPending: isDeletingApplication } =
    useDeleteApplicationMutation()
  const { mutate: createTrunkMutation, isPending: isCreatingTrunk } = useCreateTrunkMutation()
  const { mutate: updateTrunkMutation, isPending: isUpdatingTrunk } = useUpdateTrunkMutation()
  const { mutate: deleteTrunkMutation, isPending: isDeletingTrunk } = useDeleteTrunkMutation()

  const isNewTypeGuard = (s: SheetDataType | null): s is boolean | 'inbound' | 'outbound' =>
    typeof s === 'boolean' || s === 'inbound' || s === 'outbound'
  const isNew =
    isNewTypeGuard(sheetData) &&
    (sheetData === true || sheetData === 'inbound' || sheetData === 'outbound')
  const applicationItem = !isNew ? (sheetData as ApplicationTableItem) : null
  const defaultApplicationType =
    isNewTypeGuard(sheetData) && (sheetData === 'inbound' || sheetData === 'outbound')
      ? ApplicationType.SIP
      : ApplicationType.XML
  const defaultTrunkDirection =
    isNewTypeGuard(sheetData) && sheetData === 'inbound' ? 'inbound' : 'outbound'

  const isEditing = !isNew && applicationItem !== null
  const isApplication = isEditing && applicationItem.type === 'XML'
  const isTrunk =
    isEditing && (applicationItem.type === 'inbound' || applicationItem.type === 'outbound')

  // With endpoints, the user must first choose how to handle them, so the delete
  // stays invalid (button disabled) until DeleteApplicationOptions reports a choice.
  const applicationEndpointsCount = isApplication
    ? ((applicationItem?.originalData as RPlivoApplicationInternal)?.endpoints_count ?? 0)
    : 0
  const [deleteChoice, setDeleteChoice] = useState<DeleteApplicationChoice>({
    cascade: applicationEndpointsCount === 0,
    newEndpointApplication: '',
    valid: applicationEndpointsCount === 0,
  })
  const isOutboundTrunk =
    (isNew && defaultTrunkDirection === 'outbound') ||
    (isEditing && isTrunk && applicationItem.type === 'outbound')
  const isInboundTrunkForQuery =
    (isNew && defaultTrunkDirection === 'inbound') ||
    (isEditing && isTrunk && applicationItem.type === 'inbound')

  const [isOutbound, setIsOutbound] = useState<boolean>(
    isOutboundTrunk || (!isInboundTrunkForQuery && defaultTrunkDirection === 'outbound'),
  )

  const [formInstance, setFormInstance] =
    useState<GenericFormType<UnifiedApplicationFormValues> | null>(null)

  const isSipTrunk =
    formInstance?.state.values.application_type === 'sip' ||
    defaultApplicationType === ApplicationType.SIP ||
    isTrunk

  // All three Zentrunk resources are trunk-only; gate on isSipTrunk so the
  // XML application sheet (including the messaging apps page) doesn't fetch
  // them. isOutbound alone isn't sufficient because its initializer defaults
  // to true on any non-inbound sheet open, including XML.
  const { data: ipAccessControlListsData } = useIPAccessControlListsQuery(
    isOpen && isSipTrunk && isOutbound,
  )
  const { data: credentialsData } = useCredentialsQuery(isOpen && isSipTrunk && isOutbound)
  const { data: originationURIsData } = useOriginationURIsQuery(isOpen && isSipTrunk && !isOutbound)

  // Memoised, not `data?.x || []` inline: each of the three queries above is DISABLED
  // for one of the two directions, so its `data` stays undefined for the whole life of
  // the sheet and a bare `|| []` mints a new array on every render. These feed the
  // `allFields` memo, and an unstable dep there gives every inline `component:` arrow
  // a new identity, which remounts the subtree, which re-subscribes useProfileQuery
  // (staleTime 0) and refetches /profile, which re-renders the sheet and starts again.
  //
  // Memoising these is necessary but NOT sufficient on its own: the six modal handlers
  // are deps too, so the memo only holds now that they are useCallback'd (caught in
  // review by Ashish, who noted the by-reference `component: VoiceMessagingSections`
  // was what actually stopped the storm).
  const originationURIs = useMemo(() => originationURIsData?.data || [], [originationURIsData])

  const ipAccessControlLists = useMemo(
    () => ipAccessControlListsData?.data?.objects || [],
    [ipAccessControlListsData],
  )
  const credentials = useMemo(() => credentialsData?.data?.objects || [], [credentialsData])

  // Get IDs for fetching details
  const appId = isApplication
    ? (applicationItem?.originalData as RPlivoApplicationInternal)?.app_id
    : undefined
  const applicationId = isApplication
    ? (applicationItem?.originalData as RPlivoApplicationInternal)?.id
    : undefined
  const trunkId = isTrunk ? (applicationItem?.originalData as RTrunk)?.trunk_id : undefined
  // Determine if it's an inbound trunk: check trunk_direction or presence of primary_uri
  const originalTrunkData = applicationItem?.originalData as RTrunk
  const isInboundTrunk =
    isTrunk &&
    (originalTrunkData?.trunk_direction === 'inbound' || !!originalTrunkData?.primary_uri)
  const inboundTrunkId = isInboundTrunk ? originalTrunkData.id : undefined
  const inboundTrunkUuid = isInboundTrunk ? originalTrunkData.uuid : undefined

  // Fetch application/trunk details when editing
  const { data: applicationDetailData } = useApplicationDetailQuery(appId)
  const { data: trunkDetailData } = useTrunkDetailQuery(trunkId)

  // Fetch inbound trunk details for linked numbers
  const { data: inboundTrunkDetailData } = useQuery({
    queryKey: [...getAPIConsoleQueryKeys().inboundTrunks, 'detail', inboundTrunkId],
    queryFn: async () => {
      if (!inboundTrunkId) return null
      const response = await settingsService.getInboundTrunkById(inboundTrunkId)
      // Zentrunk API may return { trunk: {...} } or the trunk object directly.
      // Same helper as the create path uses, so the two cannot drift again.
      return unwrapTrunk(response.data as { trunk?: RTrunk } | RTrunk) ?? null
    },
    enabled: !!inboundTrunkId && isOpen && isEditing && isInboundTrunk,
  })

  // The platform each trunk was set up for, recorded by core (the Plivo trunk
  // record has no field for it). Only needed while editing a trunk.
  const {
    data: sipTrunkPlatformsData,
    isSuccess: platformsLoaded,
    isError: platformsFailed,
  } = useSipTrunkPlatformsQuery(isOpen && isEditing && isTrunk)
  const { mutate: recordTrunkPlatform } = useRecordSipTrunkPlatformMutation()
  const { mutate: forgetTrunkPlatform } = useForgetSipTrunkPlatformMutation()

  /**
   * Which platform this trunk belongs to: what core recorded, else the trunk's
   * own config, else custom (see resolveTrunkPlatform). Undefined only while
   * CREATING, where the select must start empty so the user picks.
   *
   * A plain string, not a memo: resolveTrunkPlatform is a find plus a couple of
   * string checks, and `ipAccessControlLists` is rebuilt every render anyway
   * (`data?.objects || []`), so a memo keyed on it would re-run every render
   * while pretending not to.
   */
  const storedTrunk = isInboundTrunk
    ? inboundTrunkDetailData || (applicationItem?.originalData as RTrunk | undefined)
    : trunkDetailData || (applicationItem?.originalData as RTrunk | undefined)
  const storedTrunkRef = getTrunkRef(storedTrunk)
  // Only once the map has SETTLED. While it is in flight the recorded value is
  // unknown, and installing the inferred fallback early is how a save could
  // overwrite an authoritative livekit row with a guessed custom (see
  // canRecordPlatform). On failure the fallback is still shown, so the sheet stays
  // usable, but canRecordPlatform then refuses to persist it unless the user picks.
  const recordedPlatform = storedTrunkRef
    ? sipTrunkPlatformsData?.data?.platforms?.[storedTrunkRef]?.platform
    : undefined
  const resolvedTrunkPlatform =
    isEditing && isTrunk && (platformsLoaded || platformsFailed)
      ? resolveTrunkPlatform({
          recorded: recordedPlatform,
          primaryUri: storedTrunk?.primary_uri?.uri,
          attachedAclName: storedTrunk?.ipacl_uuid
            ? ipAccessControlLists.find((acl) => acl.ipacl_uuid === storedTrunk.ipacl_uuid)?.name
            : undefined,
        })
      : undefined

  /** Guards every platform write: see canRecordPlatform for why each case counts. */
  const mayRecordPlatform = () =>
    canRecordPlatform({
      isEditing,
      platformsLoaded,
      platformTouched: !!formInstance?.state.fieldMeta.sip_platform?.isTouched,
      // a record this build cannot read is still a record: the value on screen is
      // an inferred fallback, so writing it back would destroy the newer one
      recordedUnrecognized: isUnrecognizedRecordedPlatform(recordedPlatform),
    })

  /**
   * The recorded platform and the ACL list arrive from their own requests, after
   * the form has already been built. It must NOT be fed through defaultValues:
   * GenericForm resets the WHOLE form whenever the defaultValues identity changes
   * (generic-form.tsx: `useEffect(() => form.reset(), [defaultValues, form])`), so
   * a late arrival mid-edit would wipe everything the user had typed.
   *
   * Instead it is applied to the one field, and only while that field is still
   * pristine, so a deliberate choice is never overwritten. setFieldMeta backfills
   * errorMap: TanStack 0.28 leaves it undefined on a field it has not validated
   * yet, and the first linked validation then crashes on errorMap['onChange']
   * (failure mode 23).
   */
  useEffect(() => {
    if (!isEditing || !isTrunk || !formInstance || !resolvedTrunkPlatform) return
    if (formInstance.state.values.application_type !== 'sip') return
    if (formInstance.state.fieldMeta.sip_platform?.isTouched) return
    if (formInstance.getFieldValue('sip_platform') === resolvedTrunkPlatform) return
    formInstance.setFieldValue('sip_platform', resolvedTrunkPlatform)
    formInstance.setFieldMeta('sip_platform', (prev) => ({
      ...prev,
      errorMap: prev?.errorMap ?? {},
      errors: prev?.errors ?? [],
      isTouched: false,
    }))
  }, [resolvedTrunkPlatform, formInstance, isEditing, isTrunk])

  // Modal handlers for IP ACL and Credential management. useCallback is not
  // decoration here: all six are dependencies of the `allFields` memo below, so as
  // plain consts they were recreated every render and the memo could never hold,
  // whatever else was memoised. Their only captures are setState functions, which
  // React keeps identity-stable, so the dep arrays are empty (bar formInstance).
  const handleCreateNewIPACL = useCallback(() => {
    setEditingIPACL(null)
    setIpAclModalOpen(true)
  }, [])

  const handleEditIPACL = useCallback((ipacl: IPAccessControlList) => {
    setEditingIPACL(ipacl)
    setIpAclModalOpen(true)
  }, [])

  const handleCreateNewCredential = useCallback(() => {
    setEditingCredential(null)
    setCredentialModalOpen(true)
  }, [])

  const handleEditCredential = useCallback((credential: Credential) => {
    setEditingCredential(credential)
    setCredentialModalOpen(true)
  }, [])

  const handleCreateNewOriginationURI = useCallback(() => {
    setEditingOriginationURI(null)
    setUriModalPlatform(
      (formInstance?.state.values.sip_platform as SipPlatformKey | undefined) ?? null,
    )
    setOriginationURIModalOpen(true)
  }, [formInstance])

  const handleEditOriginationURI = useCallback((uri: ROriginationURIInternal) => {
    setEditingOriginationURI(uri)
    setUriModalPlatform(detectPlatformFromUri(uri.uri))
    setOriginationURIModalOpen(true)
  }, [])

  /**
   * Managed platforms (Retell/Vapi/ElevenLabs) authenticate via a Plivo-managed
   * IP ACL: reuse the account's copy if it carries the platform's IPs, otherwise
   * provision it now. Returns the uuid to attach, or undefined after toasting
   * the reason. Used by create AND update — switching an existing trunk to a
   * managed platform in edit has to attach the ACL too, or the trunk would be
   * saved with none.
   */
  const attachManagedAcl = async (platformKey: SipPlatformKey): Promise<string | undefined> => {
    const aclName = SIP_PLATFORMS[platformKey].managedAclName || ''
    try {
      const accountAcls = await fetchAllAccountAcls(settingsService.getIPAccessControlLists)
      const resolution = resolveManagedAcl(platformKey, accountAcls)
      if (resolution.ipaclUuid) return resolution.ipaclUuid
      if (resolution.createPayload) {
        const created = await settingsService.createIPAccessControlList(resolution.createPayload)
        queryClient.invalidateQueries({
          queryKey: getAPIConsoleQueryKeys().ipAccessControlLists,
        })
        if (created.data?.ipacl_uuid) return created.data.ipacl_uuid
      }
    } catch (error) {
      const errorText = `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
      errorToast({
        title: `Couldn't set up the ${aclName} IP access control list`,
        description: errorText || 'Please try again. If it keeps failing, contact support.',
      })
      return undefined
    }
    errorToast({
      title: `Couldn't set up the ${aclName} IP access control list`,
      description: 'Please try again. If it keeps failing, contact support.',
    })
    return undefined
  }

  const handleUnifiedSubmit = async (formData: UnifiedApplicationFormValues) => {
    // Refuse to create a trunk on the locked Custom platform. The footer button is
    // disabled in this state too, but that is presentation: this is the check that
    // holds if anything ever submits the form programmatically. No toast, because the
    // explanation card is already on screen directly under the select.
    if (isLockedCustomPlatform(formData, isEditing, isCustomPlatformLocked)) return
    try {
      if (formData.application_type === 'xml') {
        // Handle XML Application
        const applicationData: CreateApplicationFormValues = {
          app_name: formData.name,
          default_number_app: formData.default_number_app,
          default_endpoint_app: formData.default_endpoint_app,
          log_incoming_messages: formData.log_incoming_messages,
          subaccount: resolveSubaccountPayload(formData.subaccount, isEditing && isApplication),
          public_uri: formData.public_uri,
        }

        // Only include URL and method if URL has a value
        if (formData.answer_url != null) {
          applicationData.answer_url = formData.answer_url
          applicationData.answer_method = formData.answer_method
        }
        if (formData.hangup_url != null) {
          applicationData.hangup_url = formData.hangup_url
          applicationData.hangup_method = formData.hangup_method
        }
        if (formData.message_url != null) {
          applicationData.message_url = formData.message_url
          applicationData.message_method = formData.message_method
        }
        if (formData.fallback_answer_url != null) {
          applicationData.fallback_answer_url = formData.fallback_answer_url
          applicationData.fallback_method = formData.fallback_method
        }

        if (isEditing && isApplication && applicationItem) {
          const appData = applicationItem.originalData as RPlivoApplicationInternal
          updateApplicationMutation(
            {
              app_id: appData.app_id,
              payload: applicationData,
            },
            {
              onSuccess: () => {
                successToast({
                  title: 'Success',
                  description: `${appData.app_name} Application updated successfully`,
                })
                closeSheet()
              },
              onError: (error) => {
                const errorText =
                  `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
                errorToast({
                  title: 'Error',
                  description: errorText || 'Failed to update application',
                })
              },
            },
          )
        } else {
          createApplicationMutation(applicationData, {
            onSuccess: () => {
              successToast({ title: 'Success', description: 'Application created successfully' })
              closeSheet()
            },
            onError: (error) => {
              const errorText = `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
              errorToast({
                title: 'Error',
                description: errorText || 'Failed to create application',
              })
            },
          })
        }
      } else {
        // Handle SIP Trunk
        const trunkDirection = formData.trunk_direction || 'outbound'

        if (trunkDirection === 'inbound') {
          // TypeScript now knows formData is InboundTrunkFormValues when trunkDirection is 'inbound'
          const inboundFormData = formData as InboundTrunkFormValues
          const inboundTrunkPayload: CreateInboundTrunkPayload = {
            name: inboundFormData.name,
            enabled: inboundFormData.enabled ?? true,
            primary_uri: inboundFormData.primary_uri,
            ...(inboundFormData.fallback_uri && { fallback_uri: inboundFormData.fallback_uri }),
            ...(inboundFormData.message_url && {
              message_url: inboundFormData.message_url,
              message_method: inboundFormData.message_method || 'POST',
            }),
          }

          if (isEditing && isTrunk && applicationItem) {
            const trunkOriginalData = applicationItem.originalData as RTrunk
            const trunkId = trunkOriginalData.id || trunkOriginalData.uuid

            if (trunkDirection === 'inbound') {
              // Backend expects phone numbers in E.164 format (e.g., "+18552503959")
              let linkedNumbersPayload: string[] | undefined

              if (formData.linked_numbers && formData.linked_numbers.length > 0) {
                linkedNumbersPayload = formData.linked_numbers
              }

              // Update inbound trunk via Core API
              const inboundFormData = formData as InboundTrunkFormValues
              const updatePayload: UpdateInboundTrunkPayload = {
                name: inboundFormData.name,
                enabled: inboundFormData.enabled ?? true,
                primary_uri: inboundFormData.primary_uri,
                ...(inboundFormData.fallback_uri && { fallback_uri: inboundFormData.fallback_uri }),
                ...(inboundFormData.message_url && {
                  message_url: inboundFormData.message_url,
                  message_method: inboundFormData.message_method || 'POST',
                }),
                ...(linkedNumbersPayload &&
                  linkedNumbersPayload.length > 0 && { linked_numbers: linkedNumbersPayload }),
              }

              settingsService
                .updateInboundTrunkInternal(trunkId, updatePayload)
                .then(async () => {
                  const trunkRef = getTrunkRef(trunkOriginalData)
                  if (trunkRef && formData.sip_platform && mayRecordPlatform()) {
                    recordTrunkPlatform({
                      trunkRef,
                      platform: formData.sip_platform,
                      trunkDirection: 'inbound',
                    })
                  }
                  successToast({
                    title: 'Success',
                    description: 'Inbound trunk updated successfully',
                  })
                  queryClient.invalidateQueries({
                    queryKey: getAPIConsoleQueryKeys().inboundTrunks,
                  })
                  closeSheet()
                })
                .catch((error) => {
                  const errorText =
                    `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
                  errorToast({
                    title: 'Error',
                    description: errorText || 'Failed to update inbound trunk',
                  })
                })
            } else {
              // Handle outbound trunk update (existing logic)
              const outboundFormData = formData as Extract<
                UnifiedApplicationFormValues,
                { trunk_direction: 'outbound' }
              >
              updateTrunkMutation(
                {
                  trunk_id: trunkOriginalData.trunk_id || '',
                  payload: {
                    name: outboundFormData.name,
                    secure: outboundFormData.secure || false,
                    ipacl_uuid: outboundFormData.ipacl_uuid || undefined,
                    credential_uuid: outboundFormData.credential_uuid || undefined,
                  },
                },
                {
                  onSuccess: () => {
                    successToast({
                      title: 'Success',
                      description: `${trunkOriginalData.name} Trunk updated successfully`,
                    })
                    closeSheet()
                  },
                  onError: (error) => {
                    const errorText =
                      `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
                    errorToast({
                      title: 'Error',
                      description: errorText || 'Failed to update trunk',
                    })
                  },
                },
              )
            }
          } else {
            // Backend expects phone numbers in E.164 format (e.g., "+18552503959")
            if (formData.linked_numbers && formData.linked_numbers.length > 0) {
              inboundTrunkPayload.linked_numbers = formData.linked_numbers
            }

            const createPayload = inboundTrunkPayload

            try {
              const created = await settingsService.createInboundTrunkInternal(createPayload)
              // the inbound response wraps the trunk as { success, trunk }
              const createdRef = getTrunkRef(unwrapTrunk(created?.data))
              if (createdRef && formData.sip_platform && mayRecordPlatform()) {
                recordTrunkPlatform({
                  trunkRef: createdRef,
                  platform: formData.sip_platform,
                  trunkDirection: 'inbound',
                })
              }
              successToast({ title: 'Success', description: 'Inbound trunk created successfully' })
              // Base key, matching the outbound mutation: creating a trunk must
              // also clear prefix-matched consumers like the onboarding probe
              // (['api-console','trunks','onboarding-probe']) — invalidating only
              // `inboundTrunks` left the "set up your first trunk" banner pinned
              // above the trunk the user just created.
              queryClient.invalidateQueries({ queryKey: getAPIConsoleQueryKeys().trunks })
              navigate('/sip-trunking/inbound', { replace: true })
              closeSheet()
            } catch (error) {
              const errorText = `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
              errorToast({
                title: 'Error',
                description: errorText || 'Failed to create inbound trunk',
              })
            }
          }
          return
        } else {
          // Handle Outbound Trunk creation via Plivo API Proxy
          const outboundFormData = formData as Extract<
            UnifiedApplicationFormValues,
            { trunk_direction: 'outbound' }
          >
          const trunkData: CreateTrunkFormValues = {
            name: outboundFormData.name,
            trunk_direction: 'outbound',
            ipacl_uuid: outboundFormData.ipacl_uuid,
            credential_uuid: outboundFormData.credential_uuid,
            secure: outboundFormData.secure,
          }

          // Same managed-ACL rule on both paths: the platform select is visible
          // in edit now, so an existing trunk can be moved onto a managed
          // platform and must come away with that platform's ACL attached.
          const platformKey = formData.sip_platform
          if (platformKey && isManagedAclPlatform(platformKey)) {
            const managedAclUuid = await attachManagedAcl(platformKey)
            if (!managedAclUuid) return
            trunkData.ipacl_uuid = managedAclUuid
          }

          if (isEditing && isTrunk && applicationItem) {
            const trunkOriginalData = applicationItem.originalData as RTrunk
            const updatePayload: Partial<CreateTrunkFormValues> = {
              ...trunkData,
              // Unchecking auth must be sent as an explicit empty string, or the
              // omitted key leaves it attached server-side (see the helper).
              ...buildOutboundDetachPatch(trunkDetailData || trunkOriginalData, trunkData),
              ...(outboundFormData.trunk_status !== undefined && {
                trunk_status: outboundFormData.trunk_status,
              }),
            }
            const trunkRef = getTrunkRef(trunkOriginalData)
            updateTrunkMutation(
              {
                trunk_id:
                  trunkOriginalData.trunk_id ||
                  trunkOriginalData.uuid ||
                  String(trunkOriginalData.id),
                payload: updatePayload,
              },
              {
                // Inside onSuccess, matching the create and inbound paths: a
                // failed update must not leave a platform recorded that was never
                // applied. (The toast below stays where it was, fired optimistically
                // as it already did before this change.)
                onSuccess: () => {
                  // Re-recorded on every save, which is also how a legacy trunk
                  // with no record acquires one.
                  if (trunkRef && formData.sip_platform && mayRecordPlatform()) {
                    recordTrunkPlatform({
                      trunkRef,
                      platform: formData.sip_platform,
                      trunkDirection: 'outbound',
                    })
                  }
                },
              },
            )
            successToast({ title: 'Success', description: 'Trunk updated successfully' })
            closeSheet()
          } else {
            createTrunkMutation(trunkData, {
              onSuccess: (createdTrunk) => {
                const createdRef = getTrunkRef(createdTrunk)
                if (createdRef && formData.sip_platform && mayRecordPlatform()) {
                  recordTrunkPlatform({
                    trunkRef: createdRef,
                    platform: formData.sip_platform,
                    trunkDirection: 'outbound',
                  })
                }
                successToast({ title: 'Success', description: 'Trunk created successfully' })
                navigate('/sip-trunking/outbound', { replace: true })
                closeSheet()
              },
              onError: (error) => {
                const errorText =
                  `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
                errorToast({ title: 'Error', description: errorText || 'Failed to create trunk' })
              },
            })
          }
        }
        closeSheet()
      }
    } catch {
      errorToast({ title: 'Error', description: 'Failed to save' })
    }
  }

  const handleDelete = () => {
    if (!isEditing || !applicationItem || (!isApplication && !isTrunk)) return

    try {
      if (isApplication) {
        const appData = applicationItem.originalData as RPlivoApplicationInternal
        deleteApplicationMutation(
          {
            app_id: appData.app_id,
            cascade: deleteChoice.cascade,
            new_endpoint_application: deleteChoice.newEndpointApplication || undefined,
          },
          generateToastsForMutation(
            {
              title: 'Success',
              description: `${appData.app_name || 'Application'} deleted successfully`,
              callback: closeSheet,
            },
            {
              title: 'Failed to Delete Application',
              description: 'Please try again later',
            },
          ),
        )
      } else if (isTrunk) {
        const trunkData = applicationItem.originalData as RTrunk
        deleteTrunkMutation(
          {
            trunk_id: trunkData.trunk_id || trunkData.uuid || String(trunkData.id),
          },
          {
            onSuccess: () => {
              // Drop the platform record too, so a reused trunk id cannot read
              // back the deleted trunk's platform.
              const trunkRef = getTrunkRef(trunkData)
              if (trunkRef) forgetTrunkPlatform(trunkRef)
              successToast({ title: 'Success', description: 'Trunk deleted successfully' })
              closeSheet()
            },
            onError: (error) => {
              const errorText = `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
              errorToast({ title: 'Error', description: errorText || 'Failed to delete trunk' })
            },
          },
        )
      }
      closeSheet()
    } catch {
      errorToast({ title: 'Error', description: 'Failed to delete item' })
    }
  }

  const getDefaultValues = (): UnifiedApplicationFormValues => {
    if (!isEditing || !applicationItem) {
      const commonFields = {
        answer_method: 'POST' as const,
        hangup_method: 'POST' as const,
        message_method: 'POST' as const,
        fallback_method: 'POST' as const,
      }

      if (defaultApplicationType === ApplicationType.XML) {
        return {
          ...commonFields,
          application_type: defaultApplicationType,
          name: `Application-${uuidv4().replace(/-/g, '').substring(0, 6)}`,
        } as UnifiedApplicationFormValues
      }

      const trunkDefaults = {
        ...commonFields,
        application_type: defaultApplicationType,
        name: `Trunk-${uuidv4().replace(/-/g, '').substring(0, 6)}`,
        linked_numbers: [],
        trunk_direction: defaultTrunkDirection,
      }

      return trunkDefaults as UnifiedApplicationFormValues
    }

    if (isApplication) {
      // Merge the reliable list row with the detail response, letting detail override
      // only where it is non-null. The detail endpoint can return null for
      // app_name/default flags on some apps; preferring it outright fed those nulls to
      // the strict form schema and blocked editing. See buildApplicationFormDefaults.
      return buildApplicationFormDefaults(
        applicationItem.originalData as RPlivoApplicationInternal,
        applicationDetailData,
      )
    } else {
      // For inbound trunks, use inboundTrunkDetailData; for outbound, use trunkDetailData
      const trunkData = isInboundTrunk
        ? inboundTrunkDetailData || (applicationItem.originalData as RTrunk)
        : trunkDetailData || (applicationItem.originalData as RTrunk)

      // Determine trunk direction: inbound has primary_uri, outbound has ipacl_uuid/credential_uuid
      const trunkDirection: 'inbound' | 'outbound' =
        trunkData?.trunk_direction || (trunkData?.primary_uri ? 'inbound' : 'outbound')

      const trunkValues = {
        application_type: 'sip',
        name: trunkData?.name || '',
        trunk_direction: trunkDirection,
        // What core recorded for this trunk, or a legacy trunk's own config as a
        // backfill (see resolveTrunkPlatform). Left UNSET when neither knows, so
        // the select opens empty and the first save records it -- defaulting to
        // custom is what offered a LiveKit trunk an IP ACL it cannot use.
        sip_platform: resolvedTrunkPlatform,
        // Seeds the outbound auth checkboxes from what the trunk actually has,
        // or the card opens with nothing checked and blocks the update.
        trunk_authentication_type: deriveOutboundAuthType(
          trunkData?.ipacl_uuid,
          trunkData?.credential_uuid,
        ),
        trunk_domain: trunkData?.trunk_domain || '',
        trunk_status: trunkData?.trunk_status, // For outbound trunks (Plivo API)
        enabled: trunkData?.enabled, // For inbound trunks (Zentrunk API)
        ipacl_uuid: trunkData?.ipacl_uuid || undefined,
        credential_uuid: trunkData?.credential_uuid || undefined,
        // Extract ID from primary_uri object (response has object with id, uuid, etc.)
        primary_uri: trunkData?.primary_uri?.id || 0,
        fallback_uri: trunkData?.fallback_uri?.id || undefined,
        secure: trunkData?.secure || false,
        message_url: trunkData?.message_url || '',
        message_method: (trunkData?.message_method as 'GET' | 'POST') || 'POST',
        // On edit, start empty — existing linked numbers are shown in the LinkedNumbersTable.
        // Dropdown is only for adding new numbers; unlinking uses the table's Unlink button.
        linked_numbers: [],
      }
      return trunkValues as UnifiedApplicationFormValues
    }
  }

  const sheetTitle = useMemo(() => {
    if (isEditing) {
      const titleText = isApplication ? 'Edit Application' : 'Edit SIP Trunk'

      if (isApplication && appId) {
        return (
          <div className="flex flex-col gap-2">
            <span>{titleText}</span>
            <div className="flex items-center gap-1 font-inter text-secondary text-p-400">
              <span>Application ID:</span>
              <span className="font-jetbrains">{appId}</span>
              <ClipboardCopyButton
                textToCopy={appId}
                tooltipText="Copy Application ID"
                size="icon"
                variant="ghost"
                iconSize={14}
                className="h-4 w-4"
                tabIndex={-1}
                aria-label="Copy Application ID"
              />
            </div>
          </div>
        )
      }

      return titleText
    }
    return isSipTrunk ? 'Create Trunk' : 'Create Application'
  }, [isEditing, isApplication, appId, isSipTrunk])
  // Memoize default values to update when detail data is fetched or when creating from different tabs
  const defaultValues = useMemo(
    () => getDefaultValues(),
    [
      isEditing,
      applicationItem,
      applicationDetailData,
      trunkDetailData,
      inboundTrunkDetailData,
      isInboundTrunk,
      defaultApplicationType,
      defaultTrunkDirection,
      // resolvedTrunkPlatform is deliberately NOT a dependency: it lands late and
      // rebuilding defaultValues resets the whole form. The effect above syncs it
      // into the single field instead.
    ],
  )

  const isLoading =
    isCreatingApplication || isUpdatingApplication || isCreatingTrunk || isUpdatingTrunk

  const allFields = useMemo(
    () =>
      getUnifiedApplicationFormFields(
        isEditing,
        ipAccessControlLists,
        credentials,
        originationURIs,
        handleCreateNewIPACL,
        handleEditIPACL,
        handleCreateNewCredential,
        handleEditCredential,
        handleCreateNewOriginationURI,
        handleEditOriginationURI,
        setIsOutbound,
        undefined,
        {
          isCustomPlatformLocked,
          isMessagingLocked: isTrunkMessagingLocked,
          onCloseHost: closeSheet,
        },
      ),
    [
      isEditing,
      ipAccessControlLists,
      credentials,
      originationURIs,
      handleCreateNewIPACL,
      handleEditIPACL,
      handleCreateNewCredential,
      handleEditCredential,
      handleCreateNewOriginationURI,
      handleEditOriginationURI,
      isCustomPlatformLocked,
      isTrunkMessagingLocked,
      // captured in gateOptions.onCloseHost. Its own closure is stable (it only calls
      // setIsOpen), but the reference is not, and until this memo actually held the
      // per-render churn hid that. useSheet memoises its context on [isOpen,
      // sheetData], so this only rebuilds when a sheet opens or closes.
      closeSheet,
    ],
  )

  return (
    <SideSheet
      className="pl-4"
      title={sheetTitle}
      size="md"
      footer={
        <div className="flex justify-between w-full gap-2">
          <div>
            {isEditing && (
              <DestructiveConfirmPopupWithPermission
                title={isApplication ? 'Delete Application' : 'Delete Trunk'}
                description={
                  isApplication && applicationItem ? (
                    <DeleteApplicationOptions
                      app={applicationItem.originalData as RPlivoApplicationInternal}
                      onChange={setDeleteChoice}
                    />
                  ) : (
                    <DeletePopupDescription
                      name={(applicationItem?.originalData as RTrunk)?.name || ''}
                      resource="trunk"
                      action="delete"
                      subText="You cannot undo this action."
                    />
                  )
                }
                disabled={isApplication ? !deleteChoice.valid : false}
                okButtonProps={{
                  onClick: handleDelete,
                }}
                loading={isDeletingApplication || isDeletingTrunk}
              >
                {isDeletingApplication
                  ? 'Deleting Application...'
                  : isDeletingTrunk
                    ? 'Deleting Trunk...'
                    : isApplication
                      ? 'Delete Application'
                      : 'Delete Trunk'}
              </DestructiveConfirmPopupWithPermission>
            )}
          </div>
          <div className="flex gap-2">
            {formInstance ? (
              <formInstance.Subscribe>
                {(state) => (
                  <Button
                    type="submit"
                    form="application-form"
                    disabled={
                      isLoading ||
                      isLockedCustomPlatform(state.values, isEditing, isCustomPlatformLocked)
                    }
                    data-testid="button-submit-application"
                  >
                    {isEditing ? 'Update' : isSipTrunk ? 'Create Trunk' : 'Create Application'}
                  </Button>
                )}
              </formInstance.Subscribe>
            ) : (
              <Button
                type="submit"
                form="application-form"
                disabled={isLoading}
                data-testid="button-submit-application"
              >
                {isEditing ? 'Update' : isSipTrunk ? 'Create Trunk' : 'Create Application'}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <GenericForm<UnifiedApplicationFormValues>
        key={
          isEditing
            ? isApplication
              ? appId
              : isInboundTrunk
                ? inboundTrunkId
                : trunkId
            : `new-${defaultApplicationType}-${defaultTrunkDirection}`
        }
        fields={[]}
        onSubmit={handleUnifiedSubmit}
        defaultValues={defaultValues}
        className="gap-4 grid-cols-2"
        id="application-form"
        includeSubmitButton={false}
        onMount={(form: GenericFormType<UnifiedApplicationFormValues>) => {
          setFormInstance(form)
          // Explicitly set default values when creating to ensure they're applied
          if (!isEditing) {
            const currentValues = form.state.values
            if (currentValues.application_type !== defaultApplicationType) {
              form.setFieldValue('application_type', defaultApplicationType)
            }
            if (
              defaultApplicationType === 'sip' &&
              'trunk_direction' in currentValues &&
              currentValues.trunk_direction !== defaultTrunkDirection
            ) {
              form.setFieldValue('trunk_direction', defaultTrunkDirection)
              setIsOutbound(defaultTrunkDirection === 'outbound')
            } else if (
              defaultApplicationType === 'sip' &&
              'trunk_direction' in currentValues &&
              currentValues.trunk_direction
            ) {
              setIsOutbound(currentValues.trunk_direction === 'outbound')
            }
          } else if ('trunk_direction' in form.state.values && form.state.values.trunk_direction) {
            setIsOutbound(form.state.values.trunk_direction === 'outbound')
          }
        }}
      >
        {formInstance && <FieldsRenderer fields={allFields} form={formInstance} />}
      </GenericForm>

      {/* IP ACL Modal */}
      <IPACLModal
        isOpen={ipAclModalOpen}
        onClose={() => {
          setIpAclModalOpen(false)
          setEditingIPACL(null)
        }}
        editData={editingIPACL}
      />

      {/* Credential Modal */}
      <CredentialModal
        isOpen={credentialModalOpen}
        onClose={() => {
          setCredentialModalOpen(false)
          setEditingCredential(null)
        }}
        editData={editingCredential}
      />

      {/* Origination URI Modal */}
      <OriginationURIModal
        // Remount per session so modal-local state (picked transport, password
        // unlock) resets naturally instead of via prop-watching effects.
        key={
          originationURIModalOpen
            ? `uri-${editingOriginationURI?.id ?? 'new'}-${uriModalPlatform ?? 'none'}`
            : 'uri-closed'
        }
        isOpen={originationURIModalOpen}
        onClose={() => {
          setOriginationURIModalOpen(false)
          setEditingOriginationURI(null)
          setUriModalPlatform(null)
        }}
        editData={editingOriginationURI}
        platform={uriModalPlatform}
      />
      {((isApplication && applicationId) || (isInboundTrunk && inboundTrunkUuid)) && (
        <LinkedNumbersTable
          applicationId={isApplication ? applicationId : inboundTrunkUuid || ''}
          applicationName={
            isApplication ? applicationDetailData?.app_name : inboundTrunkDetailData?.name
          }
          isTrunk={isInboundTrunk}
        />
      )}
      {/* Linked Endpoints Table - Only show for XML Applications */}
      {isApplication && applicationId && <LinkedEndpointsTable applicationId={applicationId} />}
    </SideSheet>
  )
}

export { ApplicationSheet }
